import { html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { Tooltip } from "bootstrap";
import { Toast } from "@/toast";
import { fetch, initTooltips } from "@/utilities";
import { searchQueryState } from "@/state/SearchQuery";
import { DodonaElement } from "@/components/meta/dodona_element";

export type SearchAction = {
    url?: string;
    filterValue?: string;
    text: string;
    action?: string;
    js?: string;
    confirm?: string;
    tooltip?: string;
    icon: string;
    id?: string;
};

/**
 * This component represents a dropdown containing a combination of SearchOptions and SearchActions
 *
 * @element d-search-actions
 *
 * @prop {(SearchOption|SearchAction)[]} actions - The array of SearchOptions/Actions to be displayed in the dropdown
 * @prop {string} filterParam - The query parameter to filter on
 */
@customElement("d-search-actions")
export class SearchActions extends DodonaElement {
    @property({ type: Array })
    actions: SearchAction[] = [];

    @property({ type: String, attribute: "filter-param" })
    filterParam: string | undefined = undefined;

    private async onActionClick(e: Event): Promise<void> {
        e.preventDefault();

        const el = e.currentTarget as HTMLElement;
        const indexRaw = el.dataset.actionIndex;

        const index = Number(indexRaw);

        const action = this.filteredActions[index];

        await this.performAction(action);
    }

    async performAction(action: SearchAction): Promise<boolean> {
        if (!action.action && !action.js && !action.url) {
            return true;
        }

        if (action.url) {
            const url: string = searchQueryState.addParametersToUrl(action.url);
            window.open(url);
            return false;
        }

        if (!action.action) {
            (0, eval)(action.js);
            return false;
        }

        if (action.confirm === undefined || window.confirm(action.confirm)) {
            const url: string = searchQueryState.addParametersToUrl(action.action);

            const response = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
            });

            const data = await response.json();
            new Toast(data.message);

            if (data.js) {
                (0, eval)(data.js);
            } else {
                searchQueryState.arrayQueryParams.clear();
                searchQueryState.queryParams.clear();
            }
        }

        return false;
    }

    get filteredActions(): SearchAction[] {
        if (!this.filterParam) {
            return this.actions;
        }

        const filterValue = searchQueryState.queryParams.get(this.filterParam);
        return this.actions.filter((action) => action.filterValue === undefined || action.filterValue === filterValue);
    }

    protected updated(): void {
        initTooltips(this);
    }

    disconnectedCallback(): void {
        const buttons = this.renderRoot.querySelectorAll<HTMLButtonElement>("[data-bs-toggle='tooltip']");
        buttons.forEach((btn) => {
            Tooltip.getInstance(btn)?.hide();
        });
        super.disconnectedCallback();
    }

    render(): TemplateResult[] {
        return this.filteredActions.map((action, index) => {
            const descriptionId = action.tooltip ? `search-action-description-${index}` : undefined;
            return html`
                <button
                    type="button"
                    class="btn btn-outline with-icon ml-2"
                    data-action-index="${index}"
                    id=${ifDefined(action.id)}
                    title=${ifDefined(action.tooltip)}
                    data-bs-toggle=${ifDefined(action.tooltip ? "tooltip" : undefined)}
                    data-bs-placement=${ifDefined(action.tooltip ? "top" : undefined)}
                    aria-describedby=${ifDefined(descriptionId)}
                    @click=${this.onActionClick}
                >
                    <i class="mdi mdi-${action.icon} mdi-18" aria-hidden="true"></i>
                    <span>${action.text}</span>
                    ${
                        action.tooltip
                            ? html`<span id="${descriptionId}" class="visually-hidden">${action.tooltip}</span>`
                            : ""
                    }
                </button>
            `;
        });
    }
}
